import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export interface ShopOption {
  id: number;
  description: string;
  availableCount: number;
  totalCount: number;
}

interface ShopSelectorDropdownProps {
  shops: ShopOption[];
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
}

export const ShopSelectorDropdown = ({
  shops,
  selectedIds,
  onSelectionChange,
}: ShopSelectorDropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange([]);
    } else {
      onSelectionChange([id]);
    }
  };

  const removeTag = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    onSelectionChange(selectedIds.filter((s) => s !== id));
  };

  const selectedShops = shops.filter((s) => selectedIds.includes(s.id));

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full min-h-10 items-center gap-2 flex-wrap rounded-card border border-line bg-surface-card px-3 py-2 text-sm"
      >
        {selectedShops.length === 0 ? (
          <span className="text-ink-muted">Geschäft</span>
        ) : (
          selectedShops.map((shop) => (
            <span
              key={shop.id}
              className="inline-flex items-center gap-1 rounded-pill bg-teal/10 border border-teal px-2 py-0.5 text-xs text-teal font-medium"
            >
              {shop.description}
              <span
                role="button"
                onClick={(e) => removeTag(e, shop.id)}
                className="hover:text-protein cursor-pointer leading-none"
              >
                ×
              </span>
            </span>
          ))
        )}
        <span className="ml-auto text-ink-muted text-xs">
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-card border border-line bg-surface-card shadow-card overflow-hidden">
          {shops.map((shop) => {
            const selected = selectedIds.includes(shop.id);
            return (
              <button
                key={shop.id}
                type="button"
                onClick={() => toggle(shop.id)}
                className={cn(
                  "flex w-full items-center justify-between px-3 py-2.5 text-sm text-left transition hover:bg-surface",
                  selected && "bg-selection",
                )}
              >
                <span className="text-ink">
                  {shop.description}: {shop.availableCount}/{shop.totalCount}
                </span>
                {selected && (
                  <span className="text-teal text-xs font-bold">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
